import React, { Component } from 'react';
import '../../App.css';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as d3 from 'd3';
import { TimeSeries } from 'pondjs';
import {
  Charts,
  ChartContainer,
  ChartRow,
  YAxis,
  AreaChart,
  Resizable,
  LineChart,
  Legend,
  styler,
} from 'react-timeseries-charts';
import ReactTooltip from 'react-tooltip';
import {
  getProductionLabels,
  getYAxisLabel,
  getDayAheadPricesCategory,
} from './i18n';
import {
  styles,
  plotConfigColor,
  plotStyle,
  getChartRowHeight,
  legendLabelStyle,
  yAxisPriceStyle,
} from './styles';
import { priceColor } from '../../styles/colors';
import { timeAxisStyle, yAxisStyle, trackerStyle } from '../../styles/plot';
import ForecastLegend from './ForecastLegend';
import ObservedLegend from './ObservedLegend';

class TimePlot extends Component {
  state = {
    dataReady: false,
    componentUpdated: false,
    data: {
      solar: [],
      wind: [],
      windOffshore: [],
    },
    plotData: {},
    currentTech: {
      solar: true,
      wind: false,
      windOffshore: false,
    },
    currentMode: 'realtime',
    currentFilter: 'energy',
    currentDateTime: new Date().getTime(),
    yScale: d3.scaleLinear().clamp(true),

    size: [400, 200],
    plotConfig: {
      unit: 'Wh',
      color: plotConfigColor,
    },
    timeSeriesChart: {
      highlight: null,
      selection: null,
      scheme: 'Paired',
      series: [],
      columnNames: [],
      seriesUpdated: true,
    },

    tracker: null,
    trackerValue: 0,
    trackerEvent: null,

    hasForecast: false,
    hasMixedForecast: false,

    yAxisWidth: 60,
  };

  componentDidUpdate() {
    if (this.props.dataReady) {
      if (!this.state.componentUpdated) this.updatePlotData();
      else {
        let stateupdated =
          JSON.stringify(this.props.currentTech) ===
            JSON.stringify(this.state.currentTech) &&
          this.props.currentMode === this.state.currentMode &&
          this.props.currentFilter === this.state.currentFilter &&
          this.props.currentDateTime === this.state.currentDateTime &&
          this.props.data === this.state.data;
        if (!stateupdated) {
          this.setState({ componentUpdated: false });
        }
      }
    } else {
      if (this.state.componentUpdated) {
        this.setState({ componentUpdated: false });
      }
    }
  }

  updatePlotData() {
    const name = 'Production';

    const actualDate = new Date();
    const currentSelectedDate = new Date(this.props.currentDateTime);
    const hasForecast =
      currentSelectedDate.toDateString() === actualDate.toDateString() ||
      currentSelectedDate.getTime() > actualDate.getTime();

    let hasMixedForecast = false;
    if (hasForecast) {
      if (currentSelectedDate.toDateString() === actualDate.toDateString())
        hasMixedForecast = true;
    }
    let plotData = [];
    let transitionPending = false;

    if (hasForecast) {
      if (hasMixedForecast) {
        this.props.data.map((d) => {
          transitionPending = false;
          let originalValues = d.values.map((obj) => {
            if (!transitionPending) {
              if (new Date(obj.datetime).getTime() > actualDate.getTime()) {
                transitionPending = true;
              }
              return { datetime: obj.datetime, value: obj.value };
            } else return { datetime: obj.datetime, value: null };
          });
          let originalDataSeries = {
            name: d.name,
            values: originalValues,
          };
          plotData.push(originalDataSeries);
          let forecastValues = d.values.map((obj) => {
            if (new Date(obj.datetime).getTime() >= actualDate.getTime()) {
              return { datetime: obj.datetime, value: obj.value };
            } else {
              return { datetime: obj.datetime, value: null };
            }
          });
          let forecastDataSeries = {
            name: d.name + 'Forecast',
            values: forecastValues,
          };
          plotData.push(forecastDataSeries);
          return true;
        });
      } else {
        plotData = this.props.data.map((d) => {
          return {
            name: d.name + 'Forecast',
            values: d.values,
          };
        });
      }
    } else {
      plotData = this.props.data;
    }

    let columnNames = plotData.map((d) => d.name);
    const columns = ['time', ...columnNames];

    let points = [];
    for (let i = 0; i < plotData[0].values.length; i++) {
      const t = plotData[0].values[i].datetime;
      const point = [t];
      plotData.map((d) => {
        point.push(d.values[i].value);
        return true;
      });
      points.push(point);
    }
    const currentDateTime = this.props.currentDateTime;
    const series = new TimeSeries({ name, columns, points });
    const timeInSeries = plotData[0].values.filter(
      (obj) => obj.datetime.getTime() === currentDateTime.getTime(),
    );

    const trackerEvent = series.atTime(timeInSeries[0].datetime);

    this.setState((previousState, props) => {
      return {
        dataReady: true,
        componentUpdated: true,
        currentDateTime: this.props.currentDateTime,
        currentTech: this.props.currentTech,
        currentMode: this.props.currentMode,
        currentFilter: this.props.currentFilter,

        data: this.props.data,
        plotData: plotData,
        timeSeriesChart: {
          ...previousState.timeSeriesChart,
          series: series,
          columnNames: columnNames,
          seriesUpdated: true,
        },
        tracker: this.props.currentDateTime,
        trackerValue: this.props.trackerValue,
        trackerEvent: trackerEvent,
        hasForecast: hasForecast,
        hasMixedForecast: hasMixedForecast,
      };
    });
  }

  render() {
    const { classes } = this.props;
    const hasPrices = this.props.prices?.length > 0;

    if (
      !this.props.dataReady ||
      !this.state.dataReady ||
      !this.state.componentUpdated
    ) {
      return (
        <div className={classes.chartDiv}>
          <CircularProgress className={classes.progress} size={50} />
        </div>
      );
    }
    const localeOption = 'nl-BE';

    const cols = { up: this.state.timeSeriesChart.columnNames, down: [] };
    let maxSeries = 0;
    if (this.state.dataReady) {
      for (let i = 0; i < this.state.plotData.length; i++) {
        let maxPlotData = d3.max(this.state.plotData[i].values, function (t) {
          return t.value;
        });
        if (maxPlotData > maxSeries) maxSeries = maxPlotData;
      }
    }
    let max = 1;
    let yLabelOffset = 10;
    let yAxisFormat = 'd';
    const yAxisWidth = 40;

    if (maxSeries > 1) {
      max = Math.pow(10, Math.ceil(Math.log10(maxSeries)));
      while (maxSeries < max / 2) max = max / 2;
      if (max < 5) {
        yAxisFormat = (number) => {
          return number.toLocaleString('nl-BE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        };
      } else {
        if (max < 10)
          yAxisFormat = (number) => {
            return number.toLocaleString('nl-BE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          };
      }
    } else {
      max = Math.pow(10, Math.floor(Math.log10(maxSeries)));
      yAxisFormat = (number) => {
        return number.toLocaleString('nl-BE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      };
      do {
        max = max * 2;
      } while (maxSeries > max);
      if (max < 0.5)
        yAxisFormat = (number) => {
          return number.toLocaleString('nl-BE', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          });
        };
    }

    const interpolationType = 'curveCatmullRom';

    const series = this.state.timeSeriesChart.series;

    const { observedTitle, forecastTitle, production } = getProductionLabels(
      this.props.lang,
    );

    const tooltip = (
      <ReactTooltip
        id="timeplot_tooltip"
        type="light"
        className={classes.toolTip}
      >
        {!this.state.hasForecast || this.state.hasMixedForecast
          ? observedTitle
          : ''}
        {!this.state.hasForecast || this.state.hasMixedForecast ? (
          <ObservedLegend
            classes={this.props.classes}
            lang={this.props.lang}
            currentTech={this.props.currentTech}
          ></ObservedLegend>
        ) : (
          ''
        )}
        {this.state.hasForecast ? forecastTitle : ''}
        {this.state.hasForecast ? (
          <ForecastLegend
            classes={this.props.classes}
            lang={this.props.lang}
            currentTech={this.props.currentTech}
          ></ForecastLegend>
        ) : (
          ''
        )}
        {hasPrices ? (
          <Legend
            type="line"
            align="left"
            stack={true}
            style={styler([
              {
                key: 'price',
                color: priceColor,
              },
            ])}
            labelStyle={legendLabelStyle}
            categories={getDayAheadPricesCategory()}
            className={classes.timeplotLegend}
          />
        ) : (
          ''
        )}
      </ReactTooltip>
    );

    const isRealtime = this.state.currentMode === 'realtime';

    let currentPlotValue = 0;
    let previousValue = 0;
    const currentDateTime = this.props.currentDateTime;
    this.state.plotData.map((data) => {
      let currentValuePerPlot = data.values.filter(
        (obj) => obj.datetime.getTime() === currentDateTime.getTime(),
      )[0];
      if (currentValuePerPlot != null) {
        // 20181208 there is an overlap to make the curves meet between realtime and forecast, at this point the values do not need to be summed
        if (previousValue !== currentValuePerPlot.value) {
          currentPlotValue += currentValuePerPlot.value;
        }
        previousValue = currentValuePerPlot.value;
      }
      return true;
    });
    if (currentPlotValue < 0.01)
      currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 3,
      });
    else {
      if (currentPlotValue < 5) {
        currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });
      } else {
        if (currentPlotValue < 50) {
          currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
        } else {
          currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
        }
      }
    }

    const yAxisLabel = getYAxisLabel(
      this.props.currentMode === 'realtime',
      this.props.currentFilter,
    );

    const yAxisPriceLabel = `Elektriciteitsprijs (c€/kWh)${
      isRealtime && !hasPrices ? ' *' : ''
    }`;
    // €/mWh to c€/kWh
    const yAxisPriceFormat = (price) => {
      return price.toLocaleString('nl-BE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const pricesSeries = new TimeSeries({
      name: 'prices',
      columns: ['time', 'price'],
      points: this.props.prices.map((price) => [
        new Date(price.datetime),
        // convert €/gWh to c€/kWh
        price.value / 10,
      ]),
    });

    const maxPrice = pricesSeries.max('price');
    const minPrice = pricesSeries.min('price');
    const minY = Math.min(0, minPrice);
    const chartRowHeight = getChartRowHeight(this.props.windowWidth);

    return (
      <div className={classes.container}>
        <div className={classes.chartDiv}>
          <div className={classes.plotHeader + ' darkbluegreen'}>
            <div>
              {currentPlotValue} GWh {production} en bijhorende groothandelprijs
            </div>
          </div>
          {tooltip}
          <Resizable className={classes.chart}>
            <ChartContainer
              timeRange={isRealtime ? series.range() : series.range().setEnd(d3.utcDay.offset(series.end(), 10))}
              format={isRealtime ? d3.utcFormat('%Hu') : d3.utcFormat('%b')}
              showGrid={false}
              showGridPosition="under"
              // 20190408 updated to set the correct number of ticks
              timeAxisTickCount={
                isRealtime ? 5 : this.state.plotData[0].values.length + 1
              }
              timeAxisStyle={timeAxisStyle}
              trackerPosition={this.state.currentDateTime}
              trackerStyle={trackerStyle}
            >
              <ChartRow height={chartRowHeight}>
                <YAxis
                  id="value"
                  label={yAxisLabel}
                  labelOffset={yLabelOffset}
                  min={0}
                  max={max}
                  width={yAxisWidth}
                  type="linear"
                  style={yAxisStyle}
                  format={yAxisFormat}
                  tickCount={3}
                />

                <Charts>
                  <AreaChart
                    axis="value"
                    style={plotStyle}
                    series={series}
                    breakArea={false}
                    columns={cols}
                    stack={false}
                    fillOpacity={0.2}
                    opacity={0.2}
                    interpolation={interpolationType}
                    highlight={this.state.timeSeriesChart.highlight}
                  ></AreaChart>
                  {hasPrices && isRealtime ? (
                    <LineChart
                      axis="price"
                      style={{
                        price: {
                          normal: {
                            stroke: priceColor,
                            fill: 'none',
                            strokeWidth: 2,
                          },
                        },
                      }}
                      series={pricesSeries}
                      columns={['price']}
                      opacity={1}
                      breakLine={false}
                    ></LineChart>
                  ) : (
                    <g></g>
                  )}
                </Charts>
                {isRealtime ? (
                  <YAxis
                    id="price"
                    label={yAxisPriceLabel}
                    labelOffset={-yLabelOffset}
                    min={minY}
                    max={maxPrice}
                    width={yAxisWidth}
                    type="linear"
                    style={yAxisPriceStyle}
                    format={yAxisPriceFormat}
                    tickCount={3}
                  />
                ) : (
                  []
                )}
              </ChartRow>
            </ChartContainer>
          </Resizable>
          {isRealtime && !hasPrices ? (
            <p className={classes.pricesDisclaimer}>
              * Rond 13u publiceren we de elektriciteitsprijzen die morgen op de
              groothandelsmarkt verwacht worden
            </p>
          ) : (
            ''
          )}
          <div style={{ textAlign: 'right', width: '100%', marginTop: '10px' }}>
            <div className={classes.indicatorTooltipContainer + ' darkgrey'}>
              <div
                className={classes.legendBox}
                data-for="timeplot_tooltip"
                data-tip="timeplot_tooltip"
              >
                Legende
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

TimePlot.propTypes = {
  classes: PropTypes.object.isRequired,
  prices: PropTypes.arrayOf(
    PropTypes.shape({
      datetime: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
};

export default withStyles(styles)(TimePlot);
